import _ from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Box, Button, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { dispatch } from 'store/store';
import { useSelector } from 'react-redux';
import { App, Form, Spin } from 'antd';
import { AutoIcon } from 'assets/icons/common';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { setMaxRecordedDate } from 'store/reducer/lease-management/leaseSlice';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import { useControlData } from '../../../lease-v9/lease-record-management-v9/Create';
import { constantFormValues } from '../../../lease-v9/lease-record-management-v9/root';
import LeaseRecordManagementV9EditSchedule from './LeaseRecordManagementV9EditSchedule';
import LeaseRecordManagementV9EditBasicInfo from './LeaseRecordManagementV9EditBasicInfo';
import { stateUpdater, utcConverter, isGrossRentLessThanAdvanceOnEachItem } from '../utils/helperFunc';

dayjs.extend(utc);

const leaseDataCache = {};

const LeaseRecordManagementV9Edit = ({ screen = 'REVISE' }) => {
	const { message: messageApi } = App.useApp();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { state } = useLocation();

	const { lynFormProperties } = useControlData();

	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();
	const leaseTableDataFetched = useRef(false);
	const [selectHistory, setSelectHistory] = useState(null);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(false);

	const [selectedTab, setSelectedTab] = useState(0);
	const [tabInstanceId] = useState(() => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
	const pinStorageKey = `lease-record-management-v9-new-version-edit:pinned:${state?._id ?? 'unknown'}`;
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed with these edits?',
		},
	});

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);

	const companyCode = creatorOrganizationId;

	const [getLeaseTableData] = useGetQueryV3Mutation();
	const cacheKey = screen?.toLowerCase() === 'view' ? `${state?._id}-${state?.processCode}` : null;
	const initialCachedData = cacheKey ? leaseDataCache[cacheKey] : null;
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);

	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();
	const displayLeaseData = cachedLeaseData || singleLeaseData;
	useEffect(() => {
		if (singleLeaseData) {
			setCachedLeaseData(singleLeaseData);
		}
	}, [singleLeaseData]);

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	const { profileData } = useSelector((state) => state?.profile);

	const statusData = useMemo(() => {
		if (!singleLeaseData || !profileData) {
			return {};
		}

		if (screen?.toLowerCase() === 'revise') {
			const isWipParkedByApprover =
				singleLeaseData.activationStatus === 'W-I-P' &&
				singleLeaseData.recordStatus === 'parked' &&
				profileData.userId === singleLeaseData.approverUserId?.[0];

			return isWipParkedByApprover ? singleLeaseData.parkedData : singleLeaseData.data;
		}

		return singleLeaseData.data || {};
	}, [singleLeaseData, profileData, screen]);

	useEffect(() => {
		setDataFullyLoaded(false);

		getSingleLeaseData({
			suffixCode: '',
			_id: state?._id ?? '',
			operation: 'get-single',
			processCode: state?.processCode,
		});
	}, []);

	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record revised successfully', toastConfig);
			navigate(-1);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Revision not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	useEffect(() => {
		if (!statusData || Object.keys(statusData).length === 0) return;

		const viewValues = {
			...statusData,
			tabIndex: '1',
			modificationSequence: statusData?.modificationSequence,
			leaseId: statusData?.leaseId,
			leaseType: statusData?.leaseType,
			leaseTitle: statusData?.leaseTitle,
			leaseTitle_new: statusData?.leaseTitle,
			lessorName: statusData?.lessorName,
			paymentFrequency: statusData?.paymentFrequency,
			paymentFrequency_new: statusData?.paymentFrequency,
			paymentModality: statusData?.paymentModality,
			paymentModality_new: statusData?.paymentModality,
			creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
			lastModifiedDate: dayjs(statusData?.lastModifiedDate),

			advanceAmount: statusData?.advanceAmount,
			advanceAmount_new: statusData?.advanceAmount,
			advanceDuration: statusData?.advanceDuration,
			grossRent: statusData?.grossRent,
			leaseTerm: statusData?.leaseTerm,
			leaseTerm_new: statusData?.leaseTerm,
			leaseStartDate: dayjs(statusData?.leaseStartDate),
			leaseEndDate: dayjs(statusData?.leaseEndDate),
			leaseEndDate_new: dayjs(statusData?.leaseEndDate),
			escalationPercentage: statusData?.escalationPercentage || 0,
			initialDiscountRate: statusData?.initialDiscountRate,
			initialDiscountRate_new: statusData?.initialDiscountRate,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			upfrontLeasePayment_new: statusData?.upfrontLeasePayment,
			initialDirectCost: statusData?.initialDirectCost,
			initialDirectCost_new: statusData?.initialDirectCost,
			securityDeposit: statusData?.securityDeposit,
			securityDeposit_new: statusData?.securityDeposit,
			endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),

			version: String(statusData?.version),
			leaseLiability: statusData?.leaseLiability,
			leaseName: statusData?.leaseName,
			month: statusData?.month,
			agreementFiles: statusData?.agreementFiles,
			discountRateChanges: statusData?.discountRateChanges,
			advanceAmounts: statusData?.advanceAmounts,
			advanceAmounts_new: statusData?.advanceAmounts,
			grossRents: statusData?.grossRents,
			grossRents_new: statusData?.grossRents,
			grossRentList: statusData?.grossRentList,
			grossRentList_new: statusData?.grossRentList,
			grossRentListSet: statusData?.grossRentListSet,
			advanceAmountListSet: statusData?.advanceAmountListSet,
			advanceAmountList: statusData?.advanceAmountList,
			advanceAmountList_new: statusData?.advanceAmountList,
			cashIncentiveAmount: statusData?.cashIncentiveAmount,
			cashIncentiveAmount_new: statusData?.cashIncentiveAmount,

			changes: statusData?.changes?.map((item) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					...change,
					inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
				};
			}),
		};

		form.setFieldsValue(viewValues);

		setTimeout(() => {
			setDataFullyLoaded(true);
		}, 500);
	}, [statusData]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();
			const formData = form.getFieldsValue(true);
			// console.log(formData, 'formData123');

			// Prepare data for submission
			let grossRentListSet = formData?.grossRentListSet || [];
			let advanceAmountListSet = formData?.advanceAmountListSet || [];

			// grossRentListSet?.push(formData?.grossRentList);
			// advanceAmountListSet?.push(formData?.advanceAmountList);

			// Keep the existing changes structure, but update values
			// console.log('ÃƒÂ°Ã…Â¸Ã…Â¡Ã¢â€šÂ¬ ~ changesPayload ~ formData.changes:', formData.changes);
			const changesPayload = formData.changes?.map((change, index) => {
				// For the last row, include all updated fields
				if (index === formData.changes.length - 1) {
					return {
						...change,
						inputChangeAdvance: formData?.advanceAmounts?.reduce((acc, curr) => acc + curr, 0) || 0,
						inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate),
						upfrontLeasePayment: formData?.upfrontLeasePayment,
						initialDirectCost: formData?.initialDirectCost,
						securityDeposit: formData?.securityDeposit,
						inputChangeDiscountRate: formData?.initialDiscountRate,
						inputChangeLeaseTerm: formData?.leaseTerm,
						inputChangeGrossRents: formData?.grossRents,
						inputChangeEscalation: 0,
						paymentFrequency: formData?.paymentFrequency,
						paymentTime: formData?.paymentModality,
						inputChangeAdvanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
						inputChangeAdvanceAmounts: formData?.advanceAmounts,
					};
				}
				return change;
			});
			const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents_new, formData?.advanceAmounts_new);

			if (!isGrossRentAndAdvanceAmountValid) {
				messageApi.error('Inconsistency between Lease Payment and Advance Adjustment');
				return;
			}

			// Create revision payload - keeping the structure but updating values
			const revisePayload = {
				...formData,
				leaseStartDate: utcConverter(formData?.leaseStartDate),
				leaseTitle: formData?.leaseTitle,
				lessorName: formData?.lessorName,
				leaseTerm: formData?.leaseTerm,
				leaseEndDate: utcConverter(formData?.leaseEndDate),
				paymentModality: formData?.paymentModality,
				paymentModality_new: formData?.paymentModality,
				leaseType: formData?.leaseType,
				initialDiscountRate: formData?.initialDiscountRate,
				grossRentList: formData?.grossRentList,
				paymentFrequency: formData?.paymentFrequency,
				paymentFrequency_new: formData?.paymentFrequency,
				advanceAmountList: formData?.advanceAmountList,
				advanceAmount: formData?.advanceAmount,
				advanceAmounts: formData?.advanceAmounts,
				upfrontLeasePayment: formData?.upfrontLeasePayment,
				initialDirectCost: formData?.initialDirectCost,
				securityDeposit: formData?.securityDeposit,
				grossRents: formData?.grossRents,
				cashIncentiveAmount: formData?.cashIncentiveAmount,
				changes: changesPayload,
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,
				lastModifiedDate: utcConverter(dayjs()),
				grossRentListSet,
				advanceAmountListSet,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
				modifyCount: statusData?.modifyCount,
				editCount: statusData?.editCount + 1,
				//static value check for revise
				isRevised: screen === 'REVISE' ? true : false,
			};

			// Show confirm modal
			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to edit this lease record?');
			updateFormState('submitPayload', revisePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	const handleModalSubmit = (selectedDate) => {
		// Only proceed if we have a valid submitPayload with changes
		if (formState?.submitPayload?.changes && formState.submitPayload.changes.length > 0) {
			// Create a deep copy of the current submitPayload
			const updatedPayload = JSON.parse(JSON.stringify(formState.submitPayload));

			// Get the last change index
			const lastChangeIndex = updatedPayload.changes.length - 1;

			const modificationSequence = updatedPayload?.modificationSequence;
			if (modificationSequence === 0) {
				updatedPayload.changes[0].inputChangeEffectiveDate = utcConverter(dayjs(formState?.submitPayload?.leaseStartDate));
			} else {
				// Update the effective date
				// updatedPayload.changes[lastChangeIndex].inputChangeEffectiveDate = utcConverter(selectedDate);
			}

			// Calculate the length difference
			const advanceAmountLength = updatedPayload.changes[lastChangeIndex]?.inputChangeAdvanceAmounts?.length || 0;
			const grossRentLength = updatedPayload.changes[lastChangeIndex]?.inputChangeGrossRents?.length || 0;

			// Create zeros array if needed
			if (grossRentLength > advanceAmountLength) {
				const zerosArray = Array(grossRentLength - advanceAmountLength).fill(0);

				// Create a new array with the additional zeros
				updatedPayload.changes[lastChangeIndex].inputChangeAdvanceAmounts = [
					...(updatedPayload.changes[lastChangeIndex]?.inputChangeAdvanceAmounts || []),
					...zerosArray,
				];
			}

			// console.log('ÃƒÂ°Ã…Â¸Ã…Â¡Ã¢â€šÂ¬ ~ handleModalSubmit ~ updatedPayload:', updatedPayload);
			// Update the state with the new payload using the setter
			updateFormState('submitPayload', updatedPayload);

			// After updating state, make the API call
			setTimeout(() => {
				updateApi({
					operation: 'update-single',
					processCode: state?.processCode,
					suffixCode: '',
					data: updatedPayload, // Use the updated payload directly
					_id: state._id,
					version: +singleLeaseData?.version,
				});
			}, 0);
		} else {
			// If no changes, just call the API with current state
			updateApi({
				operation: 'update-single',
				processCode: state?.processCode,
				suffixCode: '',
				data: formState?.submitPayload,
				_id: state._id,
				version: +singleLeaseData?.version,
			});
		}
	};

	const handleFormValuesChange = () => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.handleDataLoading) {
				actionButtonsRef.current.handleDataLoading();
			}
		}
	};
	useEffect(() => {
		if (singleLeaseData?.data?.version && !leaseTableDataFetched.current) {
			leaseTableDataFetched.current = true;

			const version = String(singleLeaseData?.data?.version);

			getLeaseTableData({
				companyCode,
				fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
				fieldValues: [companyCode, state._id, version],
				name: 'leaseSchedulesOnVersion-v6',
			}).then((res) => {
				const results = res?.data?.results || [];
				const relevantStatuses = ['Pending', 'Recorded', 'Reversed'];
				const relevantItems = _.filter(results, (item) => relevantStatuses.includes(item.processStatus));
				const latestItem = _.maxBy(relevantItems, 'date');
				const overallMaxDate = latestItem?.date;

				dispatch(
					setMaxRecordedDate({
						minimumDateLimit: overallMaxDate,
						leaseId: String(singleLeaseData?.data?.leaseId),
					})
				);
			});
		}
	}, [singleLeaseData, companyCode, state._id, getLeaseTableData]);

	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);

		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};
	useEffect(() => {
		if (displayLeaseData && displayLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = displayLeaseData.activationStatus;
		}
	}, [displayLeaseData]);

	useEffect(() => {
		setIsHeaderPinned(sessionStorage.getItem(pinStorageKey) !== '0');
	}, [pinStorageKey]);

	const handleAutoPinToggle = () => {
		const nextState = !isHeaderPinned;
		setIsHeaderPinned(nextState);
		sessionStorage.setItem(pinStorageKey, nextState ? '1' : '0');
	};

	const tabItem = [
		{
			key: '1',
			label: 'Basic Information',
			children: (
				<>
					<LeaseRecordManagementV9EditBasicInfo
						statusData={statusData}
						singleLeaseData={singleLeaseData}
						updateFormState={updateFormState}
						vendorList={lynFormProperties?.vendorList}
					/>
				</>
			),
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<LeaseRecordManagementV9EditSchedule
						screen={screen}
						ref={leaseScheduleRef}
						viewData={singleLeaseData}
						dataFullyLoaded={dataFullyLoaded}
						key={`lease-schedule-${singleLeaseData?.data?.leaseId || 'new'}-${tabInstanceId}`}
					/>
				</>
			),
		},
	];

	return (
		<Box>
			{isViewLoading || !singleLeaseData ? (
				<Spin spinning={true} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }} />
			) : (
				<Spin spinning={isUpdateLoading || isViewLoading} style={{ maxHeight: '100%' }}>
					<Box
						sx={{
							mb: 2,
							backgroundColor: isHeaderPinned ? 'background.main' : 'transparent',
							top: isHeaderPinned ? '60px' : 'auto',
							zIndex: isHeaderPinned ? 20 : 'auto',
							position: isHeaderPinned ? 'sticky' : 'static',
							px: { xs: 2, md: 2.5 },
							pt: { xs: 1.75, md: 2 },
							bgcolor: '#fff',
							border: '1px solid #e6ebf2',
							borderRadius: '10px',
							boxShadow: 'none',
						}}
					>
						<Stack
							direction={{ xs: 'column', md: 'row' }}
							alignItems={{ xs: 'flex-start', md: 'center' }}
							justifyContent="space-between"
							spacing={2}
							sx={{ mb: 1.25 }}
						>
							<Box>
								<Typography sx={{ color: '#1f2937', fontSize: '18px', fontWeight: 600, lineHeight: 1.2 }}>
									Edit {state?.processSubName || state?.processName}
								</Typography>
							</Box>

							<Tooltip title={isHeaderPinned ? 'Pinned' : 'Unpin'}>
								<Button
									onClick={handleAutoPinToggle}
									aria-label={isHeaderPinned ? 'Unpin header' : 'Pin header'}
									sx={{
										minWidth: '36px',
										width: '36px',
										height: '36px',
										borderRadius: '10px',
										color: isHeaderPinned ? '#1D4ED8' : '#374151',
										border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#E5E7EB'}`,
										backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
									}}
								>
									<AutoIcon />
								</Button>
							</Tooltip>
						</Stack>

						<Box
							sx={{
								px: { xs: 0.25, md: 0.5 },
								borderTop: '1px solid #eef2f7',
							}}
						>
							<Tabs
								value={selectedTab}
								onChange={selectTabHandler}
								variant="scrollable"
								scrollButtons="auto"
								allowScrollButtonsMobile
								sx={{
									minHeight: '36px',
									'& .MuiTabs-indicator': {
										height: '2px',
										backgroundColor: '#2563eb',
										borderRadius: '999px',
									},
									'& .MuiTab-root': {
										minHeight: '36px',
										px: { xs: 1.25, md: 1.5 },
										mr: { xs: 0.5, md: 1 },
										textTransform: 'none',
										fontSize: '14px',
										fontWeight: 500,
										color: '#667085',
									},
									'& .Mui-selected': {
										color: '#2563eb !important',
										fontWeight: 600,
									},
								}}
							>
								{tabItem?.map((item) => (
									<Tab key={item.key} label={item.label} />
								))}
							</Tabs>
						</Box>
					</Box>

					<Form
						form={form}
						layout="vertical"
						autoComplete="off"
						requiredMark={false}
						onFinish={handleSubmit}
						onValuesChange={handleFormValuesChange}
					>
						<AntdValuePreview form={form} show={false} />

						{tabItem?.map((item, index) => (
							<Box
								key={index}
								style={{
									display: selectedTab === index ? 'block' : 'none',
								}}
							>
								{item.children}
							</Box>
						))}

						{formState?.showFormSubmitModal && (
							<ComptrollerConfirmModal
								modalData={formState?.modalData}
								handleSubmit={handleModalSubmit}
								setShowModal={setShowFormSubmitModal}
								showModal={formState?.showFormSubmitModal}
							/>
						)}

						{selectHistory && (
							<IdentityCaseActivityModal
								title={state?.processName}
								selectItem={selectHistory}
								setSelectHistory={setSelectHistory}
								processCode={singleLeaseData?.processCode}
								processVersion={singleLeaseData?.parkProcessVersion}
							/>
						)}
					</Form>
				</Spin>
			)}
		</Box>
	);
};

export default LeaseRecordManagementV9Edit;
